"use client";

import { ClerkProvider, useAuth, useUser } from "@clerk/nextjs";
import { ConvexReactClient } from "convex/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { usePathname, useSearchParams } from "next/navigation";
import { ThemeProvider } from "next-themes";
import posthog from "posthog-js";
import type { ReactNode } from "react";
import { Suspense, useEffect, useRef } from "react";
import {
  captureFirstTouchSignupAttribution,
  clearSignupAttribution,
} from "@/lib/signupAttribution";

if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
  throw new Error("Missing NEXT_PUBLIC_CONVEX_URL");
}

const convex = new ConvexReactClient(process.env.NEXT_PUBLIC_CONVEX_URL);
const POSTHOG_IDENTIFIED_USER_STORAGE_KEY =
  "darsi_posthog_identified_user_id";

function readPersistedPostHogUserId(): string | null {
  try {
    return window.localStorage.getItem(POSTHOG_IDENTIFIED_USER_STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistPostHogUserId(userId: string | null): void {
  try {
    if (userId === null) {
      window.localStorage.removeItem(POSTHOG_IDENTIFIED_USER_STORAGE_KEY);
    } else {
      window.localStorage.setItem(POSTHOG_IDENTIFIED_USER_STORAGE_KEY, userId);
    }
  } catch {
    // Analytics identity bookkeeping must never affect authentication.
  }
}

function PostHogBridge() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { isLoaded, isSignedIn, user } = useUser();
  const initializedRef = useRef(false);
  const lastPageviewRef = useRef<string | null>(null);
  const identifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    captureFirstTouchSignupAttribution();
    if (initializedRef.current) {
      return;
    }
    const token = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
    const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;
    if (!token || !apiHost) {
      return;
    }
    posthog.init(token, {
      api_host: apiHost,
      defaults: "2026-01-30",
      capture_pageview: false,
      capture_pageleave: true,
      person_profiles: "always",
      session_recording: {
        maskAllInputs: true,
      },
    });
    initializedRef.current = true;
  }, []);

  useEffect(() => {
    if (!initializedRef.current || !isLoaded) {
      return;
    }
    const previousUserId =
      identifiedUserIdRef.current ?? readPersistedPostHogUserId();
    if (isSignedIn && user) {
      if (previousUserId !== null && previousUserId !== user.id) {
        posthog.reset();
        clearSignupAttribution();
      }
      posthog.identify(user.id, {
        clerk_user_id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        first_name: user.firstName ?? undefined,
        last_name: user.lastName ?? undefined,
      });
      identifiedUserIdRef.current = user.id;
      persistPostHogUserId(user.id);
      return;
    }
    if (previousUserId !== null) {
      posthog.reset();
      identifiedUserIdRef.current = null;
      lastPageviewRef.current = null;
      persistPostHogUserId(null);
      clearSignupAttribution();
    }
  }, [isLoaded, isSignedIn, user]);

  useEffect(() => {
    captureFirstTouchSignupAttribution();
    if (!initializedRef.current || !pathname || !isLoaded) {
      return;
    }
    const query = searchParams.toString();
    const pageKey = query.length > 0 ? `${pathname}?${query}` : pathname;
    if (lastPageviewRef.current === pageKey) {
      return;
    }
    lastPageviewRef.current = pageKey;
    posthog.capture("$pageview", {
      path: pathname,
      query,
      current_url: window.location.href,
    });
  }, [isLoaded, pathname, searchParams]);

  useEffect(() => {
    if (!initializedRef.current) {
      return;
    }
    const onWindowError = (event: ErrorEvent) => {
      posthog.captureException(event.error ?? new Error(event.message), {
        source: "window_error",
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
      });
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));
      posthog.captureException(reason, { source: "unhandled_rejection" });
    };
    window.addEventListener("error", onWindowError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      window.removeEventListener("error", onWindowError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return null;
}

export function ConvexClientProvider({ children }: { children: ReactNode }) {
  return (
    <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
      <Suspense fallback={null}>
        <PostHogBridge />
      </Suspense>
      {children}
    </ConvexProviderWithClerk>
  );
}

export function AppClerkProvider({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <ClerkProvider
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        signInFallbackRedirectUrl="/dashboard"
        signUpFallbackRedirectUrl="/dashboard"
      >
        <ConvexClientProvider>{children}</ConvexClientProvider>
      </ClerkProvider>
    </ThemeProvider>
  );
}
